import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { METRICS, type MetricKey } from '@raptor/shared';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ReadingsChart } from './ReadingsChart';
import { computeRange, RANGE_PRESET_OPTIONS } from '../range';
import type { ChartCardConfig, ChartRangePreset } from '../types';

// Only charted metrics are offered — qAnalytics (charted:false) is never
// selectable, which is what keeps it off the dashboard.
const METRIC_OPTIONS = (Object.keys(METRICS) as MetricKey[])
  .filter((k) => METRICS[k].charted)
  .map((k) => ({ value: k, label: METRICS[k].label }));

// Custom range needs date inputs — deferred; the picker offers the presets.
const PRESET_OPTIONS = RANGE_PRESET_OPTIONS.filter((o) => o.value !== 'custom');

interface Props {
  card: ChartCardConfig;
  wells: Array<{ id: string; name: string }>;
  onChange: (patch: Partial<ChartCardConfig>) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}

function MiniSelect({
  value,
  onChange,
  options,
  testId,
  width = 'w-36',
}: {
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
  testId: string;
  width?: string;
}) {
  return (
    <div className={width}>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testId} className="h-8">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function ReadingsChartCard({
  card,
  wells,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}: Props) {
  const range = computeRange(card, Date.now());

  return (
    <Card data-testid={`chart-card-${card.id}`}>
      <CardHeader className="gap-2 pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <MiniSelect
              testId={`chart-card-well-${card.id}`}
              value={card.wellId}
              onChange={(v) => onChange({ wellId: v })}
              options={wells.map((w) => ({ value: w.id, label: w.name }))}
              width="w-40"
            />
            <MiniSelect
              testId={`chart-card-metric-${card.id}`}
              value={card.metric}
              onChange={(v) => onChange({ metric: v as MetricKey })}
              options={METRIC_OPTIONS}
            />
            <MiniSelect
              testId={`chart-card-range-${card.id}`}
              value={card.rangePreset}
              onChange={(v) => onChange({ rangePreset: v as ChartRangePreset })}
              options={PRESET_OPTIONS}
            />
          </div>
          <div className="flex items-center gap-1">
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              data-testid={`chart-card-up-${card.id}`}
              disabled={!canMoveUp}
              onClick={onMoveUp}
              aria-label="Move card up"
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              data-testid={`chart-card-down-${card.id}`}
              disabled={!canMoveDown}
              onClick={onMoveDown}
              aria-label="Move card down"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              data-testid={`chart-card-remove-${card.id}`}
              onClick={onRemove}
              aria-label="Remove card"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ReadingsChart wellId={card.wellId} metric={card.metric} range={range} />
      </CardContent>
    </Card>
  );
}
